import type { Icons } from '@/components/base/BaseIcon.vue'

/**
 * Link variables available in the email editor.
 *
 * - `requiresUrl: true`  → the variable wraps a URL parameter (e.g. {{customLink(<url>)}}).
 * - `requiresUrl: false` → the variable is a static token (e.g. {{rewardLink}}, {{customerApp}}).
 *
 * Add a new entry here once the backend can resolve it via MessageTagService.
 */
export interface LinkVariable {
  key: string
  /** i18n key for the short dropdown label */
  label: string
  /**
   * i18n key for the description shown under the label in the dropdown items.
   * Also reused as the input placeholder when a static variable is selected.
   */
  description: string
  /** i18n key for the success toast shown when the link is added */
  successMessage: string
  /** Icon shown in the dropdown trigger and each menu item */
  icon: Icons
  requiresUrl: boolean
}

export const linkVariables: LinkVariable[] = [
  {
    key: 'customLink',
    label: 'pages.email.emailTemplateCreatePage.linkVariables.label.customLink',
    description: 'global.customVars.customLink.description',
    successMessage: 'pages.email.emailTemplateCreatePage.linkVariables.success.customLink',
    icon: 'HeroLinkIconOutline',
    requiresUrl: true
  },
  {
    key: 'customerApp',
    label: 'pages.email.emailTemplateCreatePage.linkVariables.label.customerApp',
    description: 'global.customVars.customerApp.description',
    successMessage: 'pages.email.emailTemplateCreatePage.linkVariables.success.customerApp',
    icon: 'HeroDevicePhoneMobileIconOutline',
    requiresUrl: false
  }
  // Tags non encore branchés côté back (à réactiver quand MessageService::sendEmailContentMail
  // les ajoute à tagsToReplace) :
  // {
  //   key: 'rewardLink',
  //   label: 'pages.email.emailTemplateCreatePage.linkVariables.label.rewardLink',
  //   description: 'global.customVars.rewardLink.description',
  //   successMessage: 'pages.email.emailTemplateCreatePage.linkVariables.success.rewardLink',
  //   icon: 'HeroGiftIconOutline',
  //   requiresUrl: false
  // },
  // {
  //   key: 'reviewLink',
  //   label: 'pages.email.emailTemplateCreatePage.linkVariables.label.reviewLink',
  //   description: 'global.customVars.reviewLink.description',
  //   successMessage: 'pages.email.emailTemplateCreatePage.linkVariables.success.reviewLink',
  //   icon: 'HeroStarIconOutline',
  //   requiresUrl: false
  // }
]

export interface DetectedVariable {
  variable: LinkVariable
  url: string
}

export function detectLinkVariable(value: string | null | undefined): DetectedVariable | null {
  if (!value) return null

  for (const v of linkVariables) {
    if (v.requiresUrl) {
      const re = new RegExp(`^\\s*\\{\\{\\s*${v.key}\\(\\s*(.*?)\\s*\\)\\s*\\}\\}\\s*$`, 'i')
      const match = re.exec(value)
      if (match) return { variable: v, url: match[1] ?? '' }
    } else {
      const re = new RegExp(`^\\s*\\{\\{\\s*${v.key}\\s*\\}\\}\\s*$`, 'i')
      if (re.test(value)) return { variable: v, url: '' }
    }
  }

  return null
}

export function buildLinkValue(variable: LinkVariable, url: string): string {
  if (variable.requiresUrl) {
    return url ? `{{${variable.key}(${url})}}` : ''
  }
  return `{{${variable.key}}}`
}
